/**
 * Persistent board markers for NetworkDuelScene: target glow, action glow,
 * tribute spiral, the "can still attack" sword, and the Equip Spell link glyph.
 */

import { Scene } from 'phaser'
import { AnimationSpeed } from '../AnimationSpeed'

const GLOW_TEX = 'board-glow'
const SWORD_TEX = 'atk-sword'
const EQUIP_TEX = 'equip-link'
const CARD_W = 64
const CARD_H = 92

export type TargetGlowKind = 'selectable' | 'picked' | 'alert'

function motionReduced(): boolean {
  return (
    typeof matchMedia === 'function' &&
    matchMedia('(prefers-reduced-motion: reduce)').matches
  )
}

/**
 * Builds the shared glow / sword / Equip-link textures if this scene does not
 * have them yet.
 */
export function ensureMarkerTextures(scene: Scene): void {
  if (!scene.textures.exists(GLOW_TEX)) {
    const g = scene.add.graphics()
    g.fillStyle(0xffffff, 1)
    g.fillCircle(16, 16, 16)
    g.generateTexture(GLOW_TEX, 32, 32)
    g.destroy()
  }
  if (!scene.textures.exists(SWORD_TEX)) {
    const g = scene.add.graphics()
    // Blade.
    g.fillStyle(0xe2e8f0, 1)
    g.fillTriangle(14, 1, 10, 26, 18, 26)
    g.fillRect(12, 22, 4, 8)
    // Gold cross-guard.
    g.fillStyle(0xfbbf24, 1)
    g.fillRect(5, 29, 18, 4)
    // Hilt + pommel.
    g.fillStyle(0x92400e, 1)
    g.fillRect(12, 33, 4, 8)
    g.fillStyle(0xf59e0b, 1)
    g.fillCircle(14, 42, 3)
    g.generateTexture(SWORD_TEX, 28, 46)
    g.destroy()
  }
  if (!scene.textures.exists(EQUIP_TEX)) {
    drawEquipLinkTexture(scene)
  }
}

/**
 * Green plus-in-a-ring, close to the Equip Spell type icon on the card frame.
 */
function drawEquipLinkTexture(scene: Scene): void {
  const g = scene.add.graphics()
  const cx = 24
  const cy = 24
  g.fillStyle(0x4ade80, 0.28)
  g.fillCircle(cx, cy, 22)
  // Four arms of the plus, leaving a hole for the ring.
  g.fillStyle(0x16a34a, 1)
  g.fillRect(cx - 5, 4, 10, 13)
  g.fillRect(cx - 5, 31, 10, 13)
  g.fillRect(4, cy - 5, 13, 10)
  g.fillRect(31, cy - 5, 13, 10)
  g.fillStyle(0x4ade80, 1)
  g.fillRect(cx - 4, 6, 8, 11)
  g.fillRect(cx - 4, 31, 8, 11)
  g.fillRect(6, cy - 4, 11, 8)
  g.fillRect(31, cy - 4, 11, 8)
  g.lineStyle(3.2, 0xbbf7d0, 1)
  g.strokeCircle(cx, cy, 7)
  g.lineStyle(2, 0x166534, 1)
  g.strokeCircle(cx, cy, 4.5)
  g.generateTexture(EQUIP_TEX, 48, 48)
  g.destroy()
}

/**
 * Soft gold halo for a card the player can act on right now (summon, set,
 * activate, change position, attack). Sits behind the art so only the rim shows.
 * Target-selection glow stays blue and takes priority over this.
 */
export function addActionGlow(
  scene: Scene,
  card: Phaser.GameObjects.Container,
): void {
  const glow = scene.add.image(0, 0, GLOW_TEX)
  glow.setDisplaySize(CARD_W + 30, CARD_H + 30)
  glow.setTint(0xf5d76e)
  glow.setBlendMode(Phaser.BlendModes.ADD)
  glow.setAlpha(0.34)
  const ring = scene.add
    .rectangle(0, 0, CARD_W + 10, CARD_H + 10, 0x000000, 0)
    .setStrokeStyle(3, 0xf5d76e)
  card.addAt(glow, 0)
  card.addAt(ring, 1)

  const reduce =
    typeof matchMedia === 'function' &&
    matchMedia('(prefers-reduced-motion: reduce)').matches
  if (reduce) {
    glow.setAlpha(0.4)
    return
  }
  scene.tweens.add({
    targets: glow,
    alpha: { from: 0.2, to: 0.5 },
    duration: AnimationSpeed.getDuration(880),
    yoyo: true,
    repeat: -1,
    ease: 'Sine.easeInOut',
  })
  glow.once('destroy', () => scene.tweens.killTweensOf(glow))
}

/**
 * Pulsing additive halo around a legal (or already-picked) effect / attack target.
 */
export function addTargetGlow(
  scene: Scene,
  layer: Phaser.GameObjects.Container,
  x: number,
  y: number,
  kind: TargetGlowKind,
  defense: boolean,
  scale = 1,
): Phaser.GameObjects.Container {
  const picked = kind === 'picked'
  const alert = kind === 'alert'
  const color = alert ? 0xff1a1a : picked ? 0xfacc15 : 0x88aaff
  const wrap = scene.add.container(x, y).setScale(scale)
  const glow = scene.add.image(0, 0, GLOW_TEX)
  const pad = alert ? 64 : picked ? 52 : 36
  glow.setDisplaySize(CARD_W + pad, CARD_H + pad)
  glow.setTint(color)
  glow.setBlendMode(Phaser.BlendModes.ADD)
  glow.setAlpha(alert ? 0.9 : picked ? 0.72 : 0.7)
  if (defense) glow.setAngle(90)
  wrap.add(glow)
  layer.add(wrap)

  const reduce =
    typeof matchMedia === 'function' &&
    matchMedia('(prefers-reduced-motion: reduce)').matches
  if (reduce) {
    glow.setAlpha(alert ? 0.85 : 0.7)
  } else {
    const from = alert ? 0.35 : 0.55
    const to = alert ? 1 : 0.95
    const sizeFrom = alert ? 48 : picked ? 44 : 32
    const sizeTo = alert ? 78 : picked ? 64 : 44
    scene.tweens.add({
      targets: glow,
      alpha: { from, to },
      displayWidth: { from: CARD_W + sizeFrom, to: CARD_W + sizeTo },
      displayHeight: { from: CARD_H + sizeFrom, to: CARD_H + sizeTo },
      duration: AnimationSpeed.getDuration(alert ? 380 : 720),
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    })
    glow.once('destroy', () => scene.tweens.killTweensOf(glow))
  }

  const outline = scene.add
    .rectangle(0, 0, CARD_W + 8, CARD_H + 8, 0x000000, 0)
    .setStrokeStyle(alert ? 6 : picked ? 5 : 2, color)
  if (defense) outline.setAngle(90)
  wrap.add(outline)

  if (alert) {
    const badgeY = -CARD_H / 2 - 16
    const bang = scene.add
      .text(0, badgeY, '!', {
        fontSize: '28px',
        color: '#ff2a2a',
        fontStyle: 'bold',
        stroke: '#4a0000',
        strokeThickness: 5,
      })
      .setOrigin(0.5)
    wrap.add(bang)
    if (!reduce) {
      scene.tweens.add({
        targets: bang,
        alpha: { from: 1, to: 0.2 },
        duration: AnimationSpeed.getDuration(380),
        yoyo: true,
        repeat: -1,
      })
      bang.once('destroy', () => scene.tweens.killTweensOf(bang))
    }
  }

  if (picked) {
    const badgeY = -CARD_H / 2 - 12
    const badge = scene.add.circle(0, badgeY, 11, 0x14532d, 1)
    badge.setStrokeStyle(2, 0xfacc15)
    wrap.add(badge)
    const mark = scene.add
      .text(0, badgeY, '✓', {
        fontSize: '14px',
        color: '#facc15',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
    wrap.add(mark)
  }
  return wrap
}

/**
 * Blinking red rim on a card the opponent just chose. The pointing hand is a
 * Three.js sprite, drawn above this ring.
 */
export function addEnemyTargetCue(
  scene: Scene,
  card: Phaser.GameObjects.Container,
  defense: boolean,
): void {
  const cardW = defense ? CARD_H : CARD_W
  const cardH = defense ? CARD_W : CARD_H
  const ring = scene.add
    .rectangle(0, 0, cardW + 6, cardH + 6, 0x000000, 0)
    .setStrokeStyle(4, 0xff2d2d)
  card.add(ring)
  if (motionReduced()) return
  scene.tweens.add({
    targets: ring,
    alpha: { from: 1, to: 0.08 },
    duration: AnimationSpeed.getDuration(340),
    yoyo: true,
    repeat: -1,
    ease: 'Sine.easeInOut',
  })
  ring.once('destroy', () => scene.tweens.killTweensOf(ring))
}

/**
 * Spinning amber funnel over a monster already chosen as tribute.
 */
export function addTributeSpiral(
  scene: Scene,
  layer: Phaser.GameObjects.Container,
  x: number,
  y: number,
  scale = 1,
): Phaser.GameObjects.Container {
  const wrap = scene.add.container(x, y).setScale(scale)
  const rings: {
    oy: number
    rx: number
    ry: number
    color: number
    w: number
  }[] = [
    { oy: -20, rx: 22, ry: 8, color: 0xffe082, w: 2 },
    { oy: -8, rx: 17, ry: 6.5, color: 0xffd54f, w: 2 },
    { oy: 4, rx: 12, ry: 5, color: 0xfbbf24, w: 1.5 },
    { oy: 14, rx: 7, ry: 3.5, color: 0xfb923c, w: 1.5 },
  ]
  for (const ring of rings) {
    const g = scene.make.graphics({ add: false })
    g.lineStyle(ring.w, ring.color, 0.95)
    g.strokeEllipse(0, ring.oy, ring.rx * 2, ring.ry * 2)
    wrap.add(g)
  }
  // Vertical core so it reads as a funnel, not just stacked rings.
  const core = scene.make.graphics({ add: false })
  core.lineStyle(1.5, 0xfff7ed, 0.55)
  core.lineBetween(0, -24, 0, 18)
  wrap.add(core)
  layer.add(wrap)

  scene.tweens.add({
    targets: wrap,
    angle: 360,
    duration: AnimationSpeed.getDuration(1100),
    repeat: -1,
    ease: 'Linear',
  })
  scene.tweens.add({
    targets: wrap,
    y: y - 5,
    duration: AnimationSpeed.getDuration(480),
    yoyo: true,
    repeat: -1,
    ease: 'Sine.easeInOut',
  })
  wrap.once('destroy', () => scene.tweens.killTweensOf(wrap))
  return wrap
}

/**
 * Bobbing sword on the center of a monster that can still declare an attack.
 */
export function addAttackSword(
  scene: Scene,
  card: Phaser.GameObjects.Container,
): void {
  const sword = scene.add.image(0, 0, SWORD_TEX)
  sword.setDisplaySize(32, 52)
  card.add(sword)
  scene.tweens.add({
    targets: sword,
    y: -5,
    duration: AnimationSpeed.getDuration(620),
    yoyo: true,
    repeat: -1,
    ease: 'Sine.easeInOut',
  })
  sword.once('destroy', () => scene.tweens.killTweensOf(sword))
}

/**
 * Equip-Spell glyph in the center of a card, used to show the link between
 * an Equip Spell and its host monster.
 */
export function addEquipLinkIcon(
  scene: Scene,
  card: Phaser.GameObjects.Container,
): Phaser.GameObjects.Image {
  const icon = scene.add.image(0, 0, EQUIP_TEX)
  icon.setDisplaySize(36, 36)
  icon.setName('equip-link')
  card.add(icon)
  scene.tweens.add({
    targets: icon,
    y: -4,
    duration: AnimationSpeed.getDuration(620),
    yoyo: true,
    repeat: -1,
    ease: 'Sine.easeInOut',
  })
  icon.once('destroy', () => scene.tweens.killTweensOf(icon))
  return icon
}
